import { captureException, captureMessage, logger, withScope } from "@sentry/core";
import { configureSync, getConfig, getConsoleSink, getJsonLinesFormatter, getLogger, parseLogLevel } from "@logtape/logtape";

//#region src/telem/logging.ts
const ROOT_LOG_CATEGORY = ["sentry", "mcp"];
const STDERR_CONSOLE_LEVEL_MAP = {
	trace: "error",
	debug: "error",
	info: "error",
	warning: "error",
	error: "error",
	fatal: "error"
};
let loggingConfigured = false;
function resolveLowestLevel() {
	const envLevel = typeof process !== "undefined" ? process.env.LOG_LEVEL : void 0;
	if (envLevel) try {
		return parseLogLevel(envLevel);
	} catch (error) {}
	return typeof process !== "undefined" && process.env.NODE_ENV === "development" ? "debug" : "info";
}
/**
* Creates a LogTape sink that sends logs to Sentry's Logs product using Sentry.logger.
*
* Unlike @logtape/sentry's getSentrySink which uses captureException/captureMessage
* (creating Issues), this sink uses Sentry.logger.* methods to send data to the
* Logs product.
*
* This uses @sentry/core logger API which works across all platforms (Node.js,
* Cloudflare Workers, etc.) as long as Sentry is initialized with the appropriate SDK.
*/
function createSentryLogsSink() {
	return (record) => {
		if (!logger) return;
		let message = "";
		for (let i = 0; i < record.message.length; i++) if (i % 2 === 0) message += record.message[i];
		else {
			const value = record.message[i];
			message += typeof value === "string" ? value : coerceMessage(value);
		}
		const attributes = record.properties;
		switch (record.level) {
			case "trace":
				logger.trace(message, attributes);
				break;
			case "debug":
				logger.debug(message, attributes);
				break;
			case "info":
				logger.info(message, attributes);
				break;
			case "warning":
				logger.warn(message, attributes);
				break;
			case "error":
				logger.error(message, attributes);
				break;
			case "fatal":
				logger.fatal(message, attributes);
				break;
			default: logger.info(message, attributes);
		}
	};
}
function ensureLoggingConfigured() {
	if (loggingConfigured) return;
	const consoleSink = getConsoleSink({
		formatter: getJsonLinesFormatter(),
		levelMap: STDERR_CONSOLE_LEVEL_MAP
	});
	const sentrySink = createSentryLogsSink();
	configureSync({
		reset: getConfig() !== null,
		sinks: {
			console: consoleSink,
			sentry: sentrySink
		},
		loggers: [
			{
				category: [...ROOT_LOG_CATEGORY],
				sinks: ["console", "sentry"],
				lowestLevel: resolveLowestLevel()
			},
			{
				category: ["logtape", "meta"],
				sinks: ["console"],
				lowestLevel: "warning"
			},
			{
				category: "logtape",
				sinks: ["console"],
				lowestLevel: "error"
			}
		]
	});
	loggingConfigured = true;
}
function getLogger$1(scope, defaults) {
	ensureLoggingConfigured();
	const category = Array.isArray(scope) ? scope : [scope];
	const logger$2 = getLogger([...ROOT_LOG_CATEGORY, ...category]);
	return defaults ? logger$2.with(defaults) : logger$2;
}
const ISSUE_LOGGER_SCOPE = ["runtime", "issues"];
function safeJsonStringify(value) {
	try {
		return JSON.stringify(value);
	} catch (error) {
		return void 0;
	}
}
function truncate(text, maxLength = 1024) {
	if (text.length <= maxLength) return text;
	return `${text.slice(0, maxLength - 1)}…`;
}
function coerceMessage(value) {
	if (typeof value === "string") return value;
	if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") return value.toString();
	if (value === null || value === void 0) return String(value);
	const json = safeJsonStringify(value);
	if (json) return truncate(json);
	return Object.prototype.toString.call(value);
}
function serializeError(value, depth = 0) {
	if (value instanceof Error) {
		const serialized = { message: value.message };
		if (value.name && value.name !== "Error") serialized.name = value.name;
		if (typeof value.stack === "string") serialized.stack = value.stack;
		const hasCause = "cause" in value && value.cause !== void 0;
		if (hasCause && depth < 3) {
			const cause = value.cause;
			serialized.cause = serializeError(cause, depth + 1);
		}
		return serialized;
	}
	return { message: coerceMessage(value) };
}
const logger$1 = getLogger$1([]);
const DEFAULT_LOGGER_SCOPE = [];
function buildLogProperties(level, options, serializedError) {
	const properties = { severity: level };
	if (serializedError) properties.error = serializedError;
	if (options.extra) {
		const protectedKeys = new Set([
			"severity",
			"error",
			"sentryContexts"
		]);
		const sanitizedExtra = {};
		for (const [key, value] of Object.entries(options.extra)) if (!protectedKeys.has(key)) sanitizedExtra[key] = value;
		Object.assign(properties, sanitizedExtra);
	}
	if (options.contexts) properties.sentryContexts = options.contexts;
	return properties;
}
function logWithLevel(level, value, options = {}) {
	ensureLoggingConfigured();
	const serializedError = value instanceof Error ? serializeError(value) : void 0;
	const message = serializedError ? serializedError.message : coerceMessage(value);
	const scope = options.loggerScope ?? DEFAULT_LOGGER_SCOPE;
	const scopedLogger = getLogger$1(scope, { severity: level });
	const properties = buildLogProperties(level, options, serializedError);
	switch (level) {
		case "trace":
			scopedLogger.trace(message, () => properties);
			break;
		case "debug":
			scopedLogger.debug(message, () => properties);
			break;
		case "info":
			scopedLogger.info(message, () => properties);
			break;
		case "warning":
			scopedLogger.warn(message, () => properties);
			break;
		case "error":
			scopedLogger.error(message, () => properties);
			break;
		case "fatal":
			scopedLogger.fatal(message, () => properties);
			break;
		default: scopedLogger.info(message, () => properties);
	}
}
function logDebug(value, options) {
	logWithLevel("debug", value, options);
}
function logInfo(value, options) {
	logWithLevel("info", value, options);
}
function logWarn(value, options) {
	logWithLevel("warning", value, options);
}
function logError(value, options) {
	logWithLevel("error", value, options);
}
function logIssue(error, options = {}) {
	ensureLoggingConfigured();
	const eventId = withScope((scopeInstance) => {
		if (options.contexts) for (const [key, context] of Object.entries(options.contexts)) scopeInstance.setContext(key, context);
		if (options.extra) scopeInstance.setContext("log", options.extra);
		if (options.attachments) for (const [key, data] of Object.entries(options.attachments)) scopeInstance.addAttachment({
			data,
			filename: key
		});
		const captureLevel = "error";
		return typeof error === "string" ? captureMessage(error, {
			contexts: options.contexts,
			level: captureLevel
		}) : captureException(error, {
			contexts: options.contexts,
			level: captureLevel
		});
	});
	const { attachments,...baseOptions } = options;
	const extra = {
		...baseOptions.extra ?? {},
		...attachments && Object.keys(attachments).length > 0 ? { attachments: Object.keys(attachments) } : {},
		...eventId ? { eventId } : {}
	};
	logError(error, {
		...baseOptions,
		extra,
		loggerScope: baseOptions.loggerScope ?? ISSUE_LOGGER_SCOPE
	});
	return eventId;
}

//#endregion
export { getLogger$1 as getLogger, logDebug, logError, logInfo, logIssue, logWarn, logger$1 as logger };
//# sourceMappingURL=logging-B7X3yL0T.js.map